import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Product } from "../entities/Product";
import { Skeleton } from "../components/ui/skeleton.jsx";

export default function Home() {
  const [featured3d, setFeatured3d] = useState([]);
  const [featuredApparel, setFeaturedApparel] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadFeaturedProducts();
  }, []);

  const loadFeaturedProducts = async () => {
    setIsLoading(true);
    try {
      const [data3d, dataApparel] = await Promise.all([
        Product.filter({ featured: true, category: '3d_products' }, "-created_date", 4),
        Product.filter({ featured: true, category: 'apparel' }, "-created_date", 20)
      ]);
      setFeatured3d(data3d);
      setFeaturedApparel(
        dataApparel.filter((p) => p.name !== 'Designer T-Shirt').slice(0, 4)
      );
    } catch (error) {
      console.error('Failed to load featured products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen">

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative border-b border-[var(--line)]" style={{ minHeight: "calc(100vh - 56px)" }}>
        {/* Background category images — split layout */}
        <div className="absolute inset-0 grid grid-cols-2">
          <div className="relative overflow-hidden">
            <img
              src="/3dprint1.jpg"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-20"
            />
          </div>
          <div className="relative overflow-hidden border-l border-[var(--line)]">
            <img
              src="/apparelpic1.png"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-20"
            />
          </div>
        </div>

        {/* Hero content — centred editorial */}
        <div className="relative z-10 page-wrap flex flex-col justify-between h-full py-16" style={{ minHeight: "calc(100vh - 56px)" }}>
          {/* Top annotation row */}
          <div className="flex items-center justify-between">
            <span className="mono-label">Est. 2026</span>
            <span className="mono-label">Studio Collection</span>
          </div>

          {/* Centred headline */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-5xl"
          >
            <h1 className="text-[clamp(3rem,8vw,7rem)] leading-[0.88] mb-8 text-[var(--ink)]">
              Designed with intent.<br />
              <em className="not-italic text-[var(--brand)]">Made to endure.</em>
            </h1>
            <p className="text-[var(--muted)] text-base max-w-sm font-['Space_Grotesk']">
              3D-crafted products and technical apparel. Two collections, one design language.
            </p>
          </motion.div>

          {/* Bottom CTA row */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8">
            <Link to="/products" className="btn-brand px-6 py-3 inline-flex items-center gap-3">
              Shop 3D Products
              <ArrowRight size={12} />
            </Link>
            <Link to="/apparel" className="btn-outline px-6 py-3 inline-flex items-center gap-3">
              Shop Apparel
              <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Category split ───────────────────────────────────── */}
      <section className="grid md:grid-cols-2 border-b border-[var(--line)]">
        <Link
          to="/products"
          className="group relative overflow-hidden border-r border-[var(--line)]"
          style={{ minHeight: "380px" }}
        >
          <video
            src="/stoneman.mp4"
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-8">
            <p className="mono-label mb-3 text-white/60">Category / 01</p>
            <h2 className="text-5xl text-white leading-none">3D Products</h2>
          </div>
          <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="mono-label text-[var(--brand)] border border-[var(--brand)] px-3 py-1.5">Explore →</span>
          </div>
        </Link>

        <Link
          to="/apparel"
          className="group relative overflow-hidden"
          style={{ minHeight: "380px" }}
        >
          <video
            src="/apparel_blk.mp4"
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-8">
            <p className="mono-label mb-3 text-white/60">Category / 02</p>
            <h2 className="text-5xl text-white leading-none">Apparel</h2>
          </div>
          <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="mono-label text-[var(--brand)] border border-[var(--brand)] px-3 py-1.5">Explore →</span>
          </div>
        </Link>
      </section>

      {/* ── Featured products — editorial grid ───────────────── */}
      <section className="page-wrap py-20">
        {/* Section header */}
        <div className="flex items-end justify-between mb-12 pb-6 border-b border-[var(--line)]">
          <div>
            <p className="mono-label mb-4">Featured / 2026</p>
            <h2 className="text-[clamp(2rem,5vw,4rem)] leading-none">Signature Picks</h2>
          </div>
          <Link to="/products" className="mono-label hover:text-[var(--ink)] transition-colors hidden sm:block">
            View all →
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--line)]">
            {Array(8).fill(0).map((_, i) => (
              <div key={i} className="bg-[var(--bg)] p-6 space-y-4">
                <Skeleton className="aspect-[3/4] bg-[var(--paper)]" />
                <Skeleton className="h-3 w-2/3 bg-[var(--paper)]" />
                <Skeleton className="h-3 w-1/4 bg-[var(--paper)]" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* 3D Products sub-grid */}
            {featured3d.length > 0 && (
              <div className="mb-16">
                <p className="mono-label mb-8">3D Products</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--line)]">
                  {featured3d.map((product, index) => (
                    <ProductCard key={product.id} product={product} index={index} />
                  ))}
                </div>
              </div>
            )}

            {/* Apparel sub-grid */}
            {featuredApparel.length > 0 && (
              <div>
                <p className="mono-label mb-8">Apparel</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--line)]">
                  {featuredApparel.map((product, index) => (
                    <ProductCard key={product.id} product={product} index={index} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Footer strip ─────────────────────────────────────── */}
      <footer className="border-t border-[var(--line)]">
        <div className="page-wrap py-8 flex items-center justify-between">
          <span className="mono-label">Smple Product © 2026</span>
          <Link to="/newsletter" className="mono-label hover:text-[var(--ink)] transition-colors">
            Newsletter →
          </Link>
        </div>
      </footer>
    </div>
  );
}

function ProductCard({ product, index }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: index * 0.07 }}
      className="bg-[var(--bg)] group"
    >
      <Link to={`/product/${product.id}`}>
        <div className="relative overflow-hidden aspect-[3/4]" style={{ background: product.bgColor || 'var(--paper)' }}>
          {product.inventory === 0 && (
            <div className="absolute top-3 left-3 z-10 bg-[var(--bg)] border border-[var(--line)] text-[var(--muted)] px-2.5 py-1 text-[10px] tracking-widest uppercase font-['Space_Mono']">
              Out of Stock
            </div>
          )}
          <img
            src={product.images?.[0] || 'https://images.unsplash.com/photo-1643902854732-f398c85e3b0d?auto=format&fit=crop&q=80&w=800'}
            alt={product.name}
            className={`w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.04]${product.inventory === 0 ? ' opacity-50' : ''}`}
          />
        </div>
        <div className="p-5">
          <h3 className="text-base mb-1 font-['Space_Grotesk'] font-medium group-hover:text-[var(--muted)] transition-colors">
            {product.name}
          </h3>
          <p className="font-['Space_Mono'] text-xs text-[var(--muted)]">
            ${product.price?.toFixed(2)}
          </p>
        </div>
      </Link>
    </motion.div>
  );
}
